import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner, Badge } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import config from '../config';
import AuthNavbar from "components/Navbars/AuthNavbar";
import AuthFooter from "components/Footers/AuthFooter";

const AdminOrders = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({ orderId: '', sessionId: '' });
  const [editedOrders, setEditedOrders] = useState({});
  const [showLiveOnly, setShowLiveOnly] = useState(true); // State for toggling between live/test mode

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      if (searchParams.orderId) params.append("order_id", searchParams.orderId);
      if (searchParams.sessionId) params.append("session_id", searchParams.sessionId);

      const apiUrl = `${config.apiUrl}/${config.apiStage}/admin/orders?${params.toString()}`;
      const response = await fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
      const result = await response.json();
      setOrders(result.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (order_id) => {
    try {
      const token = await getAccessTokenSilently();
      const updated = editedOrders[order_id];
      await fetch(`${config.apiUrl}/${config.apiStage}/admin/orders`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id, ...updated })
      });
      alert('Order updated!');
      fetchOrders();
    } catch (err) {
      console.error('Failed to update order', err);
    }
  };

  const handleDeleteOrder = async (order_id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        const token = await getAccessTokenSilently();
        await fetch(`${config.apiUrl}/${config.apiStage}/admin/orders/${order_id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Order deleted!');
        fetchOrders();
      } catch (err) {
        console.error('Failed to delete order', err);
      }
    }
  };

  const handleFieldChange = (order_id, field, value) => {
    setEditedOrders(prev => ({
      ...prev,
      [order_id]: { ...prev[order_id], [field]: value }
    }));
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter(order => order.livemode === showLiveOnly);

  return (
    <div className="main-content">
      <AuthNavbar />
      <div className="header bg-gradient-info pb-8 pt-7 pt-md-8 text-black">
        <Container className="mt-5">
          <h1 className="text-center">Admin Orders</h1>

          <Row className="mb-3">
            <Col md={6}>
              <Input
                placeholder="Search by Order ID"
                className="mb-3"
                value={searchParams.orderId}
                onChange={(e) => setSearchParams({ ...searchParams, orderId: e.target.value })}
              />
            </Col>
            <Col md={6}>
              <Input
                placeholder="Search by Session ID"
                value={searchParams.sessionId}
                onChange={(e) => setSearchParams({ ...searchParams, sessionId: e.target.value })}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col className="text-center">
              <Button color="info" onClick={fetchOrders}>Search</Button>
              <Button
                color={showLiveOnly ? "success" : "secondary"}
                className="ml-3"
                onClick={() => setShowLiveOnly(prev => !prev)}
              >
                Showing: {showLiveOnly ? "Live" : "Test"} Orders (Toggle)
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center"><Spinner color="info" /></div>
          ) : (
            filteredOrders.map(order => (
              <Card className="mb-3" key={order.order_id}>
                <CardBody>
                  <p>
                  <strong style={{ fontWeight: 'bold' }}>Status:</strong>{" "}
                    <Badge color={order.livemode ? "success" : "warning"}>
                      {order.livemode ? "Live" : "Test"}
                    </Badge>
                  </p>
                  <p><strong style={{ fontWeight: 'bold' }}>Order ID:</strong> {order.order_id}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Stripe ID:</strong> {order.stripe_session_id}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Quill Guuid:</strong> {order.guuid || order.stripe_session_id}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Email:</strong> {order.email}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Customer Name:</strong> {order.name}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Quill Name:</strong> {order.friendly_name}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Product:</strong> {order.product_id} / {order.selected_option}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Colors:</strong> QR - {order.qr_color}, Logo - {order.logo_color}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Quantity:</strong> {order.quantity}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Amount:</strong> ${order.amount}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Promo Code:</strong> {order.promotion_code}</p>
                  <p><strong style={{ fontWeight: 'bold' }}>Shipping:</strong></p>
                  <ul>
                    <li>{order.shipping_address.line1}</li>
                    {order.shipping_address.line2 && <li>{order.shipping_address.line2}</li>}
                    <li>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</li>
                    <li>{order.shipping_address.country}</li>
                  </ul>
                  <p><strong style={{ fontWeight: 'bold' }}>Created At:</strong> {order.created_at}</p>

                {/* Status Input */}
                <label htmlFor={`status-${order.order_id}`} className="form-label" style={{ fontWeight: 'bold' }}>
                    Status:
                </label>
                <Input
                    id={`status-${order.order_id}`} // Add a unique id to the input field
                    type="text"
                    value={
                    editedOrders[order.order_id]?.status !== undefined
                        ? editedOrders[order.order_id].status
                        : order.fulfillment_status
                    }
                    onChange={(e) => handleFieldChange(order.order_id, 'status', e.target.value)}
                    placeholder="Enter order status"
                />

                {/* Tracking Number Input */}
                <label htmlFor={`tracking-number-${order.order_id}`} className="form-label mt-2" style={{ fontWeight: 'bold' }}>
                    Tracking Number:
                </label>
                <Input
                    id={`tracking-number-${order.order_id}`} // Add a unique id to the input field
                    type="text"
                    value={
                    editedOrders[order.order_id]?.tracking_number !== undefined
                        ? editedOrders[order.order_id].tracking_number
                        : order.tracking_number || ''
                    }
                    onChange={(e) => handleFieldChange(order.order_id, 'tracking_number', e.target.value)}
                    placeholder="Enter tracking number"
                />

                  <Button color="primary" className="mt-2" onClick={() => handleUpdateOrder(order.order_id)}>Save</Button>
                  <Button color="danger" className="mt-2 ml-2" onClick={() => handleDeleteOrder(order.order_id)}>Delete</Button>
                </CardBody>
              </Card>
            ))
          )}
        </Container>
        <AuthFooter />
      </div>
    </div>
  );
};

export default AdminOrders;
